import '@fontsource/ibm-plex-sans-arabic/400.css';
import '@fontsource/ibm-plex-sans-arabic/500.css';
import '@fontsource/ibm-plex-sans-arabic/600.css';
import '@fontsource/ibm-plex-sans-arabic/700.css';
import './ui/tokens.css';
import './ui/global.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { isApiError, setUnauthenticatedHandler } from './api/client';
import { createAppRouter } from './app/router';
import { applyTheme, initialTheme, ThemeProvider } from './app/theme';
import { applyDocumentLocale, I18nProvider, initialLocale } from './i18n/I18nProvider';
import { ToastProvider } from './ui/Toast';

// Direction and theme before the first paint (no flash of the wrong layout).
applyDocumentLocale(initialLocale());
applyTheme(initialTheme());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Client errors (4xx) are answers, not glitches: never retried.
      retry: (failureCount, error) =>
        failureCount < 2 && !(isApiError(error) && error.status >= 400 && error.status < 500),
    },
    mutations: { retry: false },
  },
});

const router = createAppRouter(queryClient);

// A request found the session gone (expired, revoked): drop cached data and sign in again.
setUnauthenticatedHandler(() => {
  queryClient.clear();
  const here = `${window.location.pathname}${window.location.search}`;
  void router.navigate({
    to: '/login',
    search: { reason: 'expired', ...(here.startsWith('/login') ? {} : { redirect: here }) },
  });
});

const container = document.getElementById('root');
if (!container) throw new Error('The page has no #root element');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <ThemeProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
);
